'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ArrowRight, Loader2, MapPin, ListPlus, HandCoins, Flag as FlagIcon } from 'lucide-react';
import { useLocale, useFormatter, useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { useRegional } from '@/components/providers/regional-provider';
import { type Branding } from '@/lib/branding';
import { buildLocalePath, type Locale } from '@/i18n/config';
import type { MapboxMap, MapboxMapEvent, GeoJSONData } from '@/types/mapbox';
import { cn } from '@/lib/utils';

const MAPBOX_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_TOKEN || '';
const EVENT_DATE = new Date('2026-09-12T00:00:00');

type CountryTab = 'AU' | 'NZ';

const COUNTRY_TABS: Array<{
  code: CountryTab;
  flag: string;
  labelKey: 'australia' | 'newZealand';
}> = [
  { code: 'AU', flag: '🇦🇺', labelKey: 'australia' },
  { code: 'NZ', flag: '🇳🇿', labelKey: 'newZealand' },
];

// Globe map view configs per country tab (centre + zoom).
const COUNTRY_VIEWS: Record<CountryTab, { center: [number, number]; zoom: number }> = {
  AU: { center: [134, -27], zoom: 3.4 },
  NZ: { center: [172, -41], zoom: 4.6 },
};

interface GlobalRideRow {
  id: string;
  city: string;
  slug: string;
  stateName: string | null;
  stateCode: string | null;
  countryName: string;
  countryCode: string;
  eventDate: string | null;
  startTime: string | null;
  capacity: string | number | null;
  currentRegistrations: number;
  raised: number;
  donationCount: number;
  currencyCode: string;
  coordinates: string | null;
}

function parseCoords(value: string | null): { lat: number; lng: number } | null {
  if (!value) return null;
  const [lat, lng] = value.split(',').map((s) => Number(s.trim()));
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  return { lat, lng };
}

export interface LandingPageProps {
  branding?: Branding;
}

export function LandingPage(_props: LandingPageProps) {
  const locale = useLocale() as Locale;
  const tHero = useTranslations('uwrHero');
  const tAbout = useTranslations('uwrAbout');
  const tRideWith = useTranslations('uwrRideWith');
  const tHowTo = useTranslations('uwrHowTo');
  const tCountdown = useTranslations('hero');
  const tEvents = useTranslations('events');
  const tNav = useTranslations('nav');
  const format = useFormatter();
  const { currency: regionalCurrency } = useRegional();

  const registerHref = buildLocalePath(locale, '/register');
  const ridesAnchor = '#ride-with-us';

  // ───────── Countdown ─────────
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  useEffect(() => {
    const tick = () => {
      const diff = Math.max(0, EVENT_DATE.getTime() - Date.now());
      setCountdown({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // ───────── Rides data ─────────
  const { data: globalRidesRaw = [] } = trpc.publicLookups.globalMapRides.useQuery({
    currencyCode: regionalCurrency === 'NZD' ? 'NZD' : 'AUD',
  });
  const globalRides = globalRidesRaw as GlobalRideRow[];

  const totalRiders = useMemo(
    () => globalRides.reduce((sum, ride) => sum + Number(ride.currentRegistrations || 0), 0),
    [globalRides],
  );

  // ───────── Mapbox ─────────
  const [activeCountry, setActiveCountry] = useState<CountryTab>('AU');
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<MapboxMap | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapInitialized, setMapInitialized] = useState(false);

  // Load Mapbox JS + CSS once
  useEffect(() => {
    if (!MAPBOX_TOKEN) return;

    // One-time custom popup CSS so the close button renders as a styled circle
    // instead of the bare default `×`.
    if (!document.querySelector('style[data-mapbox-uwr-custom]')) {
      const style = document.createElement('style');
      style.setAttribute('data-mapbox-uwr-custom', 'true');
      style.textContent = `
        .mapboxgl-popup { z-index: 10; }
        .mapboxgl-popup-content {
          padding: 0 !important;
          border-radius: 12px !important;
          box-shadow: 0 10px 40px rgba(0,0,0,0.18) !important;
          overflow: visible;
        }
        .mapboxgl-popup-close-button {
          position: absolute;
          top: -12px; right: -12px;
          display: flex; align-items: center; justify-content: center;
          width: 26px; height: 26px;
          font-size: 16px; line-height: 1; font-weight: 600;
          color: #ffffff;
          background: #1c1917 !important;
          border: 2px solid #ffffff;
          border-radius: 9999px;
          padding: 0 !important;
          cursor: pointer;
          transition: transform 0.15s ease;
        }
        .mapboxgl-popup-close-button:hover { transform: scale(1.08); }
        .mapboxgl-popup-tip { border-top-color: #ffffff !important; }
        .mapboxgl-ctrl-logo, .mapboxgl-ctrl-attrib { display: none !important; }
      `;
      document.head.appendChild(style);
    }

    if (window.mapboxgl) {
      queueMicrotask(() => setMapLoaded(true));
      return;
    }

    if (!document.querySelector('link[href*="mapbox-gl"]')) {
      const link = document.createElement('link');
      link.href = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.css';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }

    const existingScript = document.querySelector('script[src*="mapbox-gl"]');
    if (existingScript) {
      if (window.mapboxgl) {
        queueMicrotask(() => setMapLoaded(true));
      } else {
        existingScript.addEventListener('load', () => setMapLoaded(true));
      }
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.js';
    script.async = true;
    script.onload = () => setMapLoaded(true);
    document.head.appendChild(script);
  }, []);

  // Initialise the map
  useEffect(() => {
    if (!mapLoaded || !mapContainer.current || !MAPBOX_TOKEN || mapInstance.current) return;

    const mapboxgl = window.mapboxgl;
    if (!mapboxgl) return;
    mapboxgl.accessToken = MAPBOX_TOKEN;

    const view = COUNTRY_VIEWS[activeCountry];
    mapInstance.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: view.center,
      zoom: view.zoom,
      minZoom: 2,
      maxZoom: 14,
    });

    mapInstance.current.on('load', () => setMapInitialized(true));

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
      setMapInitialized(false);
    };
    // Only run once on first load; subsequent country changes are handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapLoaded]);

  // Render pins for the active country.
  const renderPins = useCallback(() => {
    if (!mapInitialized || !mapInstance.current || !window.mapboxgl) return;
    const m = mapInstance.current;
    const mapboxgl = window.mapboxgl;

    if (m.getLayer('uwr-pins')) m.removeLayer('uwr-pins');
    if (m.getSource('uwr-rides')) m.removeSource('uwr-rides');

    const ridesForCountry = globalRides.filter((r) => r.countryCode === activeCountry);
    const features = ridesForCountry
      .map((ride) => {
        const coords = parseCoords(ride.coordinates);
        if (!coords) return null;
        return {
          type: 'Feature' as const,
          geometry: { type: 'Point' as const, coordinates: [coords.lng, coords.lat] },
          properties: {
            id: ride.id,
            city: ride.city,
            slug: ride.slug,
            countryName: ride.countryName,
            countryCode: ride.countryCode,
            stateName: ride.stateName ?? '',
            riders: ride.currentRegistrations,
            raised: ride.raised,
          },
        };
      })
      .filter(Boolean);

    m.addSource('uwr-rides', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features,
      } as unknown as GeoJSONData,
    });

    m.addLayer({
      id: 'uwr-pins',
      type: 'circle',
      source: 'uwr-rides',
      paint: {
        'circle-color': 'hsl(19, 88%, 56%)',
        'circle-radius': 8,
        'circle-stroke-width': 3,
        'circle-stroke-color': '#ffffff',
      } as Record<string, unknown>,
    });

    m.on('click', 'uwr-pins', (e: MapboxMapEvent) => {
      const feat = m.queryRenderedFeatures(e.point, { layers: ['uwr-pins'] });
      if (!feat.length) return;
      const props = feat[0].properties as Record<string, unknown>;
      const coords = feat[0].geometry.coordinates as unknown as [number, number];
      const formattedRaised = format.number(props.raised as number, {
        style: 'currency',
        currency: regionalCurrency,
        notation: 'compact',
        maximumFractionDigits: 0,
      });
      new mapboxgl.Popup({ offset: 14, closeButton: true })
        .setLngLat(coords)
        .setHTML(
          `<div style="padding:14px;min-width:220px;font-family:system-ui,sans-serif;">
            <h3 style="font-weight:600;margin:0 0 4px;font-size:16px;color:#1c1917;">${props.city}</h3>
            ${props.stateName ? `<p style="margin:0 0 10px;font-size:12px;color:#78716c;">${props.stateName}</p>` : ''}
            <div style="display:grid;grid-template-columns:repeat(2,1fr);gap:6px;margin-bottom:12px;font-size:11px;">
              <div>
                <p style="margin:0;color:#78716c;">${tEvents('riders')}</p>
                <p style="margin:0;font-weight:600;color:#1c1917;">${format.number(props.riders as number)}</p>
              </div>
              <div>
                <p style="margin:0;color:#78716c;">${tEvents('raised')}</p>
                <p style="margin:0;font-weight:600;color:hsl(19 88% 56%);">${formattedRaised}</p>
              </div>
            </div>
            <a href="/events/${(props.countryName as string).toLowerCase().replace(/[^a-z]+/g, '-')}/${props.slug}"
               style="display:block;text-align:center;padding:8px 14px;background:#1c1917;color:white;border-radius:8px;font-size:12px;font-weight:500;text-decoration:none;">
              ${tEvents('viewEvent')}
            </a>
          </div>`,
        )
        .addTo(m);
    });

    m.on('mouseenter', 'uwr-pins', () => {
      m.getCanvas().style.cursor = 'pointer';
    });
    m.on('mouseleave', 'uwr-pins', () => {
      m.getCanvas().style.cursor = '';
    });
  }, [mapInitialized, globalRides, activeCountry, format, regionalCurrency, tEvents]);

  // (Re-)draw pins when data, country, or map readiness changes.
  // Guard against the dev-mode StrictMode double-mount race where the map's
  // 'load' event fires before its style is fully ready — addSource/addLayer
  // silently no-op in that window.
  useEffect(() => {
    const m = mapInstance.current;
    if (!m) return;
    if (m.isStyleLoaded()) {
      renderPins();
    } else {
      m.once('idle', renderPins);
    }
  }, [renderPins]);

  // Fly to the country whenever the active tab changes.
  useEffect(() => {
    if (!mapInitialized || !mapInstance.current) return;
    const view = COUNTRY_VIEWS[activeCountry];
    mapInstance.current.flyTo({ center: view.center, zoom: view.zoom, duration: 1400 });
  }, [activeCountry, mapInitialized]);

  // ───────── Render ─────────

  return (
    <>
      {/* Hero — shorter than full viewport so the image stays compact. */}
      <section className="relative isolate flex min-h-[68vh] sm:min-h-[72vh] flex-col overflow-hidden bg-stone-950 text-white">
        <div className="absolute inset-0 z-0">
          <Image src="/images/uwr/HeroImageUWR.jpg" alt="" fill priority className="object-cover" />
          {/* Subtle left-edge shade just so the left-aligned content stays legible */}
          <div className="absolute inset-y-0 left-0 w-full sm:w-2/3 bg-gradient-to-r from-stone-950/65 via-stone-950/30 to-transparent" />
        </div>

        <div className="container relative z-10 flex-1 flex items-center pb-12 pt-28">
          <div className="max-w-xl flex flex-col items-start text-left">
            <Image
              src="/images/uwr/UWR_Logo_Date.png"
              alt="United We Ride"
              width={520}
              height={220}
              priority
              className="h-32 sm:h-40 lg:h-48 w-auto"
            />
            <p className="mt-8 text-base sm:text-lg font-extrabold uppercase tracking-[0.18em] text-white/90">
              {tHero('callToRide')}
            </p>

            <div className="mt-8">
              <Button
                size="lg"
                asChild
                className="bg-uwr-orange text-white hover:bg-uwr-orange-hover text-base px-8 h-12 rounded-full font-extrabold tracking-wide uppercase"
              >
                <Link href={ridesAnchor} prefetch={false}>
                  {tHero('rideWithUsCta')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* About + countdown / riders */}
      <section className="bg-stone-950 text-white">
        <div className="container py-14 sm:py-20">
          <div className="grid gap-10 md:grid-cols-[auto,1fr] md:items-center md:gap-14">
            <Image
              src="/images/uwr/SalvationArmyLogo.svg"
              alt="The Salvation Army"
              width={140}
              height={167}
              className="h-32 sm:h-40 w-auto mx-auto md:mx-0"
            />
            <div>
              <p className="text-base sm:text-lg leading-relaxed text-white/85">{tAbout('body')}</p>

              <div className="mt-8 grid grid-cols-2 gap-6 sm:gap-10 border-t border-white/10 pt-6">
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-white/55">
                    {tAbout('daysToGo')}
                  </p>
                  <p className="mt-2 font-bold text-3xl sm:text-4xl tabular-nums">
                    {countdown.days}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-white/55">
                    {tAbout('riders', { year: EVENT_DATE.getFullYear() })}
                  </p>
                  <p className="mt-2 font-bold text-3xl sm:text-4xl tabular-nums">
                    {format.number(totalRiders)}
                  </p>
                </div>
              </div>

              {countdown.days > 0 && (
                <div className="mt-6 flex gap-4 text-white/70 text-xs uppercase tracking-widest">
                  <span>
                    {String(countdown.hours).padStart(2, '0')} {tCountdown('countdownHours')}
                  </span>
                  <span>
                    {String(countdown.minutes).padStart(2, '0')} {tCountdown('countdownMinutes')}
                  </span>
                  <span>
                    {String(countdown.seconds).padStart(2, '0')} {tCountdown('countdownSeconds')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Ride With Us — Mapbox AU / NZ tabs */}
      <section id="ride-with-us" className="bg-white py-20 sm:py-24">
        <div className="container">
          <div className="text-center">
            <h2 className="font-bold text-3xl sm:text-4xl text-stone-900">{tRideWith('title')}</h2>

            {/* Country tabs */}
            <div className="mt-8 inline-flex items-center gap-2 rounded-full bg-stone-100 p-1">
              {COUNTRY_TABS.map((tab) => {
                const active = activeCountry === tab.code;
                return (
                  <button
                    key={tab.code}
                    type="button"
                    onClick={() => setActiveCountry(tab.code)}
                    className={cn(
                      'flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition-colors',
                      active ? 'bg-stone-900 text-white' : 'text-stone-600 hover:text-stone-900',
                    )}
                  >
                    <span aria-hidden>{tab.flag}</span>
                    <span>{tRideWith(tab.labelKey)}</span>
                  </button>
                );
              })}
            </div>

            <p className="mt-6 text-sm text-stone-500">{tRideWith('subtitle')}</p>
          </div>

          <div className="relative mx-auto mt-10 max-w-5xl">
            <div
              ref={mapContainer}
              className="h-[480px] sm:h-[560px] md:h-[640px] w-full rounded-2xl overflow-hidden bg-stone-100"
            />

            {!mapInitialized && MAPBOX_TOKEN && (
              <div className="absolute inset-0 flex items-center justify-center bg-stone-100 rounded-2xl">
                <div className="text-center">
                  <Loader2 className="h-8 w-8 animate-spin text-stone-400 mx-auto mb-3" />
                  <p className="text-stone-500 text-sm">{tEvents('loadingGlobalMap')}</p>
                </div>
              </div>
            )}

            {!MAPBOX_TOKEN && (
              <div className="absolute inset-0 flex items-center justify-center bg-stone-100 rounded-2xl">
                <div className="text-center">
                  <MapPin className="h-12 w-12 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500 text-sm">{tEvents('mapNotAvailable')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* How To Take Part */}
      <section className="bg-stone-50 py-20 sm:py-24">
        <div className="container max-w-5xl text-center">
          <h2 className="font-bold text-3xl sm:text-4xl text-stone-900">{tHowTo('title')}</h2>

          <div className="mt-12 grid gap-10 md:grid-cols-3">
            {[
              { icon: ListPlus, key: 'register' as const, href: registerHref },
              { icon: HandCoins, key: 'fundraise' as const, href: registerHref },
              { icon: FlagIcon, key: 'ride' as const, href: ridesAnchor },
            ].map((step) => {
              const Icon = step.icon;
              return (
                <div key={step.key} className="flex flex-col items-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-uwr-orange/10 text-uwr-orange">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 text-xl font-bold text-stone-900">
                    {tHowTo(`${step.key}Title` as const)}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-stone-600 max-w-xs">
                    {tHowTo(`${step.key}Body` as const)}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="mt-12">
            <Button
              size="lg"
              asChild
              className="bg-uwr-orange text-white hover:bg-uwr-orange-hover h-12 px-8 rounded-full font-semibold uppercase tracking-wide"
            >
              <Link href={registerHref} prefetch={false}>
                {tNav('register')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
